import {Injectable} from '@angular/core';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {User} from '../models/user.interface';
import {Master} from '../../admin/models/master.interface';
import {KfRegistrationDetail} from '../models/kf-registration-detail.interface';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {KfRegistration} from '../../shared/models/kf-registration.interface';
import {KfRegistrationSearch} from '../../shared/models/kf-registration-search.interface';
import {KfRegistrationSource} from '../models/kf-registration-source.interface';
import {Currency} from '../models/currency.interface';

const KF_REGISTRATION_API = environment.apiUrl + 'kfregistration';
const USER_API = environment.apiUrl + 'user';

@Injectable({
  providedIn: 'root'
})
export class KfRegistrationService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: KfRegistrationSearch): string {
    let searchParams = '';
    if (search.startYear && search.startYear !== '') {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear && search.endYear !== '') {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.countryId) {
      searchParams += `&countryId=${search.countryId}`;
    }
    if (search.weeeSystemId) {
      searchParams += `&weeeSystemId=${search.weeeSystemId}`;
    }
    if (search.keyFigures && search.keyFigures !== '') {
      searchParams += `&keyFigures=${search.keyFigures}`;
    }
    if (search.b2bb2c && search.b2bb2c !== '') {
      searchParams += `&b2bb2c=${search.b2bb2c}`;
    }
    if (search.status && search.status !== '') {
      searchParams += `&status=${search.status}`;
    }
    return searchParams;
  }

  static getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }

  getAllCurrency(): Observable<Currency[]> {
    return this.http.get<Currency[]>(`${KF_REGISTRATION_API}/currency`);
  }

  getAllKfRegistrations(search: KfRegistrationSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<KfRegistration>> {
    let url = `${KF_REGISTRATION_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = KfRegistrationService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfRegistrationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<KfRegistration>>(url + searchParams);
  }

  getUser(email: string): Observable<User> {
    return this.http.get<User>(`${USER_API}?email=${email}`);
  }

  getCategories(schemaKind: string, keyFigures: string, year: number, elementId: number): Observable<Master[]> {
    return this.http.get<Master[]>
    (`${KF_REGISTRATION_API}/categories?schemaKind=${schemaKind}&keyFigures=${keyFigures}&year=${year}&elementId=${elementId}`);
  }

  getKfRegistration(keyFigures: string, year: number, elementId: number, type: string): Observable<KfRegistrationDetail> {
    return this.http.get<KfRegistrationDetail>(`${KF_REGISTRATION_API}/${keyFigures}?year=${year}&elementId=${elementId}&type=${type}`);
  }

  getKfRegistrationDetailLevel(keyFigures: string, year: number, elementId: number, type: string): Observable<ApiHttpResponse<string>> {
    return this.http.get<ApiHttpResponse<string>>(`${KF_REGISTRATION_API}/${keyFigures}/detailLevel?year=${year}&elementId=${elementId}&type=${type}`);
  }

  checkPomAndCollectedValues(year: number, elementId: number, type: string): Observable<ApiHttpResponse<boolean>> {
    return this.http.get<ApiHttpResponse<boolean>>(`${KF_REGISTRATION_API}/check-pom-collected?year=${year}&elementId=${elementId}&type=${type}`);
  }

  existsPreviousYear(keyFigures: string, year: number, elementId: number, type: string): Observable<boolean> {
    return this.http.get<boolean>(`${KF_REGISTRATION_API}/${keyFigures}/exists-previous?year=${year}&elementId=${elementId}&type=${type}`);
  }

  getRegistrationSources(): Observable<KfRegistrationSource[]> {
    return this.http.get<KfRegistrationSource[]>(`${KF_REGISTRATION_API}/source`);
  }

  saveKfRegistration(kfRegistrationDetail: KfRegistrationDetail, keyfigures: string): Observable<ApiHttpResponse<KfRegistrationDetail>> {
    return this.http.post<ApiHttpResponse<KfRegistrationDetail>>(KF_REGISTRATION_API + '/' + keyfigures, kfRegistrationDetail)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<KfRegistrationDetail>) => {
          return res;
        })
      );
  }

  deleteKfRegistration(kfRegistrationDetail: KfRegistrationDetail, keyfigures: string): Observable<ApiHttpResponse<KfRegistrationDetail>> {
    return this.http.post<ApiHttpResponse<KfRegistrationDetail>>(`${KF_REGISTRATION_API}/delete` + '/' + keyfigures, kfRegistrationDetail)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<KfRegistrationDetail>) => {
          return res;
        })
      );
  }

  markAsCompleted(keyFigures: string, year: number, elementId: number, type: string): Observable<ApiHttpResponse<object>> {
    const fd = new FormData();
    fd.append('keyFigures', keyFigures);
    fd.append('year', year.toString());
    fd.append('elementId', elementId.toString());
    fd.append('type', type);
    return this.http.post<ApiHttpResponse<object>>(`${KF_REGISTRATION_API}/completed`, fd).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<object>) => {
        return res;
      })
    );
  }

  markAsConfidential(keyFigures: string, year: number, elementId: number, type: string): Observable<ApiHttpResponse<object>> {
    let params = new HttpParams();
    params = params.set('keyFigures', keyFigures);
    params = params.set('year', year.toString()); // Convertí el número a string
    params = params.set('elementId', elementId.toString()); // Convertí el número a string
    params = params.set('type', type);
    return this.http.put<ApiHttpResponse<object>>(`${KF_REGISTRATION_API}/confidential`, null, { params: params }).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<object>) => {
        return res;
      })
    );
  }

  printPdf(keyFigures: string, year: number, elementId: number, type: string): Observable<any> {
    return this.http.get(`${KF_REGISTRATION_API}/print/${keyFigures}?year=${year}&elementId=${elementId}&type=${type}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

}
